import { useState, useEffect } from 'react'

interface CurrencyInfo {
  price: string
  originalPrice: string
  currencyCode: string
  isLoading: boolean
}

// Approximate exchange rates from USD
const CURRENCY_MAP: Record<
  string,
  { code: string; symbol: string; rate: number }
> = {
  MX: { code: 'MXN', symbol: '$', rate: 17.5 },
  CO: { code: 'COP', symbol: '$', rate: 4200 },
  AR: { code: 'ARS', symbol: '$', rate: 950 },
  CL: { code: 'CLP', symbol: '$', rate: 950 },
  PE: { code: 'PEN', symbol: 'S/', rate: 3.75 },
  BR: { code: 'BRL', symbol: 'R$', rate: 5.0 },
  ES: { code: 'EUR', symbol: '€', rate: 0.92 },
  FR: { code: 'EUR', symbol: '€', rate: 0.92 },
  DE: { code: 'EUR', symbol: '€', rate: 0.92 },
  IT: { code: 'EUR', symbol: '€', rate: 0.92 },
  PT: { code: 'EUR', symbol: '€', rate: 0.92 },
  GB: { code: 'GBP', symbol: '£', rate: 0.79 },
  EC: { code: 'USD', symbol: '$', rate: 1 },
  PA: { code: 'USD', symbol: '$', rate: 1 },
  SV: { code: 'USD', symbol: '$', rate: 1 },
  GT: { code: 'GTQ', symbol: 'Q', rate: 7.75 },
  CR: { code: 'CRC', symbol: '₡', rate: 515 },
  DO: { code: 'DOP', symbol: 'RD$', rate: 58 },
  HN: { code: 'HNL', symbol: 'L', rate: 24.7 },
  NI: { code: 'NIO', symbol: 'C$', rate: 36.7 },
  BO: { code: 'BOB', symbol: 'Bs', rate: 6.91 },
  PY: { code: 'PYG', symbol: '₲', rate: 7500 },
  UY: { code: 'UYU', symbol: '$U', rate: 40 },
  VE: { code: 'USD', symbol: '$', rate: 1 }, // Venezuela uses USD informally
}

const BASE_PRICE_USD = 17
const ORIGINAL_PRICE_USD = 97

function formatLocalPrice(amount: number, code: string): string {
  // For currencies with large numbers, round to nearest whole number
  if (amount >= 100) {
    return Math.round(amount).toLocaleString('es-MX')
  }
  // For smaller currencies, show up to 2 decimals
  if (amount % 1 === 0) {
    return amount.toString()
  }
  return amount.toFixed(2)
}

function buildPriceString(
  amount: number,
  currency: { code: string; symbol: string; rate: number },
): string {
  const converted = amount * currency.rate
  return `${currency.symbol}${formatLocalPrice(converted, currency.code)} ${currency.code}`
}

// Cache the result so multiple components don't re-fetch
let cachedResult: CurrencyInfo | null = null
let fetchPromise: Promise<CurrencyInfo> | null = null

async function fetchCurrency(): Promise<CurrencyInfo> {
  try {
    const res = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000),
    })
    if (!res.ok) throw new Error('API error')
    const data = await res.json()
    const countryCode: string = data.country_code || ''
    const currency = CURRENCY_MAP[countryCode]

    if (currency && currency.code !== 'USD') {
      return {
        price: buildPriceString(BASE_PRICE_USD, currency),
        originalPrice: buildPriceString(ORIGINAL_PRICE_USD, currency),
        currencyCode: currency.code,
        isLoading: false,
      }
    }

    // Default to USD
    return {
      price: '$17 USD',
      originalPrice: '$97 USD',
      currencyCode: 'USD',
      isLoading: false,
    }
  } catch {
    return {
      price: '$17 USD',
      originalPrice: '$97 USD',
      currencyCode: 'USD',
      isLoading: false,
    }
  }
}

export function useCurrencyByIP(): CurrencyInfo {
  const [info, setInfo] = useState<CurrencyInfo>(
    cachedResult || {
      price: '$17 USD',
      originalPrice: '$97 USD',
      currencyCode: 'USD',
      isLoading: true,
    },
  )

  useEffect(() => {
    if (cachedResult) {
      setInfo(cachedResult)
      return
    }

    if (!fetchPromise) {
      fetchPromise = fetchCurrency()
    }

    fetchPromise.then((result) => {
      cachedResult = result
      setInfo(result)
    })
  }, [])

  return info
}
